import { z } from 'zod';
import { bcp47, timestamp, uuid } from './common.js';
import { documentKind, difficultyLevel, documentVersionSchema } from './document.js';
import { listQueryInput, paginated } from './list.js';
import { previewDescriptor } from './preview.js';
/**
 * Sharing / social (§4, §15.3). `shares` (ACL), `referral`, `comments`,
 * `reactions`.
 *
 * `visibility` (who can access) is ORTHOGONAL to `moderationStatus` (cleared to
 * publish) — both live on the row and are NEVER collapsed. Public discoverable
 * ONLY when `visibility='public' AND moderationStatus='approved'`.
 */
export const shareVisibility = z.enum(['private', 'public', 'link', 'user']);
export const moderationStatus = z.enum(['pending', 'approved', 'rejected']);
/** Per-document collaboration role (F64) — only when `visibility='user'`. */
export const shareRole = z.enum(['reader', 'editor']);
export const shareSchema = z.object({
    id: uuid,
    documentId: uuid,
    ownerId: uuid,
    visibility: shareVisibility,
    moderationStatus,
    /** Unguessable ≥128-bit capability token (F63) — never logged, never cached. */
    linkToken: z.string().nullable(),
    linkExpiresAt: timestamp.nullable(),
    linkRevokedAt: timestamp.nullable(),
    /** `visibility='user'`: one row per (document, shared_with_user_id). */
    sharedWithUserId: uuid.nullable(),
    shareRole: shareRole.nullable(),
    createdAt: timestamp,
    updatedAt: timestamp,
});
// --- referral (F72/F73) ------------------------------------------------------
export const referralStatus = z.enum(['pending', 'converted', 'credited', 'void']);
export const referralSchema = z.object({
    id: uuid,
    sharerUserId: uuid,
    bookId: uuid, // FK documents.id
    referredUserId: uuid.nullable(), // null until the referred user signs up
    status: referralStatus,
    credited: z.boolean(), // idempotency latch
    createdAt: timestamp,
    updatedAt: timestamp,
});
// --- comments (F28) ----------------------------------------------------------
export const commentSchema = z.object({
    id: uuid,
    shareId: uuid,
    authorUserId: uuid,
    parentCommentId: uuid.nullable(), // threaded
    body: z.string().min(1),
    /** Soft-delete (owner delete OR moderator takedown F69). */
    deletedAt: timestamp.nullable(),
    removedByUserId: uuid.nullable(),
    removedReason: z.string().nullable(),
    createdAt: timestamp,
    updatedAt: timestamp,
});
// --- reactions (F29) ---------------------------------------------------------
export const reactionKind = z.enum(['like', 'dislike']);
export const reactionSchema = z.object({
    id: uuid,
    shareId: uuid,
    userId: uuid, // UNIQUE (shareId, userId)
    kind: reactionKind,
    createdAt: timestamp,
    updatedAt: timestamp,
});
// --- input DTOs --------------------------------------------------------------
export const sharePublishInput = z
    .object({
    documentId: uuid,
    visibility: shareVisibility,
    /** Required when `visibility='user'`. */
    sharedWithUserId: uuid.optional(),
    shareRole: shareRole.optional(),
    linkExpiresAt: z.coerce.date().optional(),
})
    .refine((v) => v.visibility !== 'user' || (v.sharedWithUserId !== undefined && v.shareRole !== undefined), {
    message: 'errors:share.userGrant.incomplete',
    path: ['sharedWithUserId'],
});
export const commentCreateInput = z.object({
    shareId: uuid,
    parentCommentId: uuid.optional(),
    body: z.string().trim().min(1, 'errors:comment.body.required').max(5000, 'errors:comment.body.tooLong'),
});
/** `reaction.set` — `kind:null` clears the caller's reaction. */
export const reactionSetInput = z.object({
    shareId: uuid,
    kind: reactionKind.nullable(),
});
/**
 * `reaction.set` response — the caller's OWN resulting reaction (`null` after
 * an unset) plus the share's live aggregate counts, so the client reconciles
 * its optimistic toggle in one round trip (F29 AC: "optimistic toggle +
 * reconcile") without a second `discover.get`/`share.get` fetch.
 */
export const reactionSetResult = z.object({
    shareId: uuid,
    viewerReaction: reactionKind.nullable(),
    likeCount: z.number().int().nonnegative(),
    dislikeCount: z.number().int().nonnegative(),
});
// --- share.update / share.revoke / share.get / share.list (F27/F63/F64) ------
/**
 * `share.update` — mutates an EXISTING share row's non-ACL-defining fields
 * without re-running the moderation gate: an existing `visibility='user'`
 * grant's `shareRole` (reader↔editor), or an existing `visibility='link'`
 * share's `linkExpiresAt`. Switching `visibility` itself (private→public,
 * etc.) goes back through `share.publish` (its `refine` already enforces the
 * `user`-grant completeness invariant) — `share.update` deliberately can't
 * change `visibility`, so it can never be used to bypass the moderation gate
 * a fresh `public` publish requires.
 */
export const shareUpdateInput = z.object({
    shareId: uuid,
    shareRole: shareRole.optional(),
    linkExpiresAt: z.coerce.date().nullable().optional(),
});
export const shareUpdateResult = shareSchema;
/**
 * `share.revoke` — unpublish: sets `visibility` back to `'private'`. Does
 * NOT touch `moderationStatus` history or claw back any `share_approved`/
 * `share_public_reward` credit already awarded (F27 AC). Distinct from
 * `link.revoke` (kills only the link capability) and `share.ungrant` (kills
 * only one user grant row) — this is the "take the whole thing private" op.
 */
export const shareRevokeInput = z.object({ shareId: uuid });
export const shareRevokeResult = shareSchema;
/** `share.get` — owner/admin/grantee-authorized read of one share row (ACL enforced server-side, fail-closed §15). */
export const shareGetInput = z.object({ shareId: uuid });
export const shareSortColumns = ['createdAt', 'updatedAt'];
/** `share.list` — the "My shares" management table (design/app/35-shares.html). */
export const shareListQuery = listQueryInput(shareSortColumns, { defaultSort: 'updatedAt' }).extend({
    visibility: shareVisibility.optional(),
});
/** One `share.list` row — the share plus enough document context to render the management table without a second round trip. */
export const shareListRow = shareSchema.extend({
    documentTitle: z.string(),
    preview: previewDescriptor.nullable(),
    commentCount: z.number().int().nonnegative(),
    likeCount: z.number().int().nonnegative(),
    dislikeCount: z.number().int().nonnegative(),
});
export const shareListResult = paginated(shareListRow);
// --- share.grant / share.ungrant (F64 — user+role doc ACL) -------------------
/**
 * `share.grant` — invite one specific user as `reader` or `editor` on a
 * document. Creates (or role-updates) the `visibility='user'` row for
 * `(documentId, userId)` — the DB's `shares_document_shared_with_unique`
 * partial-unique index is what makes this idempotent per grantee. Server
 * MUST reject `userId === ownerId` (self-grant is meaningless — owner
 * already has full access) with `VALIDATION`, never silently no-op.
 */
export const shareGrantInput = z.object({
    documentId: uuid,
    userId: uuid,
    role: shareRole,
});
export const shareGrantResult = shareSchema;
/** `share.ungrant` — revoke one user's grant. Idempotent: ungranting an already-gone grant returns `revoked: false`, not an error. */
export const shareUngrantInput = z.object({ shareId: uuid });
export const shareUngrantResult = z.object({ shareId: uuid, revoked: z.boolean() });
// --- link token issue / revoke / resolve (F63) --------------------------------
/** `link.issue` — creates (or re-keys) the `visibility='link'` share for a document and mints a fresh unguessable token. */
export const linkIssueInput = z.object({
    documentId: uuid,
    linkExpiresAt: z.coerce.date().optional(),
});
/**
 * The raw capability token is readable ONLY in this response (owner-only,
 * right after issue) — `share.get`/`share.list` MUST redact `linkToken` to
 * `null` for every caller including the owner on subsequent reads (§4/§15.3:
 * "never logged, never cached" extends to "never re-served"). Consumers
 * needing the shareable URL again re-derive it from `share.get`'s
 * `linkToken===null` state by treating a non-null `linkToken` here as the
 * one-time reveal.
 */
export const linkIssueResult = shareSchema;
/** `link.revoke` — sets `linkRevokedAt`; a currently-open reader drops to the F66 access-denied gate (F27 AC). */
export const linkRevokeInput = z.object({ shareId: uuid });
export const linkRevokeResult = shareSchema;
/** `link.resolve` — `/s/:token` capability-URL open. Anonymous: no account needed, NOT moderation-gated for access (only `share.publish`'s `public` path is). */
export const linkResolveInput = z.object({ token: z.string().min(1) });
// --- discover.list / discover.get (F62) ---------------------------------------
export const discoverSortColumns = ['publishedAt'];
/** `discover.list` — the `/discover` feed: public + approved shares ONLY (§15.3, never collapsed with `search.query`'s FTS — this is category-filtered browse, not full-text search). */
export const discoverListQuery = listQueryInput(discoverSortColumns, { defaultSort: 'publishedAt' }).extend({
    categoryId: uuid.optional(),
});
const discoverCategoryRef = z.object({ id: uuid, slug: z.string(), nameKey: z.string() });
export const discoverListRow = z.object({
    shareId: uuid,
    documentId: uuid,
    ownerId: uuid,
    ownerDisplayName: z.string().nullable(),
    title: z.string(),
    kind: documentKind,
    difficultyLevel,
    language: bcp47,
    preview: previewDescriptor.nullable(),
    categories: z.array(discoverCategoryRef),
    commentCount: z.number().int().nonnegative(),
    likeCount: z.number().int().nonnegative(),
    dislikeCount: z.number().int().nonnegative(),
    publishedAt: timestamp,
});
export const discoverListResult = paginated(discoverListRow);
export const discoverGetInput = z.object({ shareId: uuid });
/**
 * The full public read view — `discover.get` (F62, gated: `visibility='public'
 * AND moderationStatus='approved'`, else `NOT_FOUND` — existence-leak
 * defense, §15/platform.md) AND `link.resolve` (F63, gated: link not
 * expired/revoked instead) return this SAME shape, since both open the same
 * F66 reader. Never partially redacted client-side — the server only ever
 * returns this once ACL + (for discover) moderation have already cleared it.
 */
export const sharePublicView = z.object({
    share: shareSchema,
    documentId: uuid,
    title: z.string(),
    kind: documentKind,
    difficultyLevel,
    language: bcp47,
    currentVersion: documentVersionSchema,
    ownerDisplayName: z.string().nullable(),
    preview: previewDescriptor.nullable(),
    categories: z.array(discoverCategoryRef),
    commentCount: z.number().int().nonnegative(),
    likeCount: z.number().int().nonnegative(),
    dislikeCount: z.number().int().nonnegative(),
    /** The CALLER's own reaction, `null` for anonymous/no-reaction viewers. */
    viewerReaction: reactionKind.nullable(),
});
export const discoverGetResult = sharePublicView;
export const linkResolveResult = sharePublicView;
// --- comment.list / comment.remove (F28) --------------------------------------
export const commentSortColumns = ['createdAt'];
/** `comment.list` — omit `parentCommentId` for the top-level thread page; pass one to page a specific reply thread. */
export const commentListQuery = listQueryInput(commentSortColumns, {
    defaultSort: 'createdAt',
    defaultDir: 'asc',
}).extend({
    shareId: uuid,
    parentCommentId: uuid.optional(),
});
/**
 * One `comment.list` row. `authorIsPro` is computed at READ time from the
 * author's CURRENT `users.tier` — never persisted on the comment row (F28
 * AC: "PRO badge updates LIVE if the author upgrades/downgrades").
 */
export const commentListRow = commentSchema.extend({
    authorDisplayName: z.string().nullable(),
    authorIsPro: z.boolean(),
    likeCount: z.number().int().nonnegative(),
    dislikeCount: z.number().int().nonnegative(),
    viewerReaction: reactionKind.nullable(),
    replyCount: z.number().int().nonnegative(),
});
export const commentListResult = paginated(commentListRow);
/** `comment.remove` — owner-delete only (soft-delete, tombstone keeps the thread, F28 AC). Moderator/admin removal is the separate `moderation.takedown` op (F69) — different audit action, reason REQUIRED there, optional here. */
export const commentRemoveInput = z.object({ commentId: uuid });
export const commentRemoveResult = z.object({ commentId: uuid, removed: z.boolean() });
// --- live pushes: share:{shareId} / presence:{shareId} (§5.5) -----------------
/**
 * Push on `share:{shareId}` — "signal, not payload" (matches
 * `documentVersionPush`/`searchIndexPush`'s convention): the client refetches
 * (`comment.list` / `discover.get` / `share.get`) on receipt rather than
 * trusting an inlined payload. Covers F28 (comment add/remove), F29
 * (reaction count change), F27/F74 (moderation verdict), F69 (takedown),
 * and `link.revoke`/`share.revoke` (`'revoked'`).
 */
export const sharePush = z.object({
    shareId: uuid,
    event: z.enum([
        'comment_added',
        'comment_removed',
        'reaction_changed',
        'moderation_verdict',
        'takedown',
        'revoked',
    ]),
});
/** Push on `presence:{shareId}` — live viewer count while reading/commenting. Deliberately minimal, no per-viewer identity broadcast. */
export const presencePush = z.object({
    shareId: uuid,
    viewerCount: z.number().int().nonnegative(),
});
// --- referral (F72/F73) -------------------------------------------------------
export const referralSortColumns = ['createdAt'];
/** `referral.list` — the sharer's own referral status list (design/app/35-shares.html / 26-activity.html). */
export const referralListQuery = listQueryInput(referralSortColumns, { defaultSort: 'createdAt' });
/**
 * One `referral.list` row. `creditsEarned` is derived from
 * `credit_ledger WHERE reason='referral_reward' AND ref_id=referral.id` at
 * read time (0 until `credited` flips) — never a second stored total, so it
 * can never drift from the ledger truth (§21.3).
 */
export const referralListRow = referralSchema.extend({
    /** Null when the source document was deleted — the row persists for audit (F72 AC). */
    bookTitle: z.string().nullable(),
    referredDisplayName: z.string().nullable(),
    creditsEarned: z.number().int().nonnegative(),
});
export const referralListResult = paginated(referralListRow);
/**
 * `referral.stats` — account-wide referral aggregate for the sharer (F72
 * `/settings/referrals` status view). Deliberately a SEPARATE op from
 * `referral.list` rather than a client-side sum over the paginated rows
 * (platform.md: never fetch-all-then-slice) — `creditsEarnedTotal` sums
 * `credit_ledger` directly, `invited`/`joined`/`credited` count `referral`
 * rows directly, both server-side and unbounded by any one page.
 */
export const referralStats = z.object({
    invited: z.number().int().nonnegative(),
    joined: z.number().int().nonnegative(),
    credited: z.number().int().nonnegative(),
    creditsEarnedTotal: z.number().int().nonnegative(),
});
/**
 * The referral attribution query param carried by a share's public/link URL
 * (F72: "the shareable public/link URL carries the sharer's referral
 * attribution"). `ref` is the `shares.id` the visitor arrived through — NOT
 * a separate minted token; `referral.sharerUserId`/`bookId` are derived
 * server-side from that share row. Exported here so the WS-SETTINGS/auth
 * squad (owns `auth.dto.ts`'s `authSignupInput`, out of this package's M3
 * ownership) can compose it into their own input DTO instead of re-typing
 * the shape — see this package's WIRING note for the exact hookup this
 * turn doesn't make.
 */
export const referralAttributionParam = z.object({ ref: uuid });
